import React, { useCallback } from 'react';
import { Button } from 'antd';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { PerformDisputeAction } from '@pay-bill/graphql';
import { Colors } from 'works/legacy/lib/style-helper';
import { catchErrorWith, rejectSimpleErrors } from '@pay-bill/services';
import { useDisputeRefetchs } from '@pay-bill/hooks';
import { BaseButton } from '@common/components';

export type StateKeys =
  | 'retract'
  | 'approve'
  | 'accept'
  | 'redirect_to_organization'
  | 'retract_redirect';

export const COLOR_STYLE_MAP: Record<StateKeys, string> = {
  retract: Colors.darkGrey,
  approve: Colors.elm,
  accept: Colors.elm,
  retract_redirect: Colors.darkGrey,
  redirect_to_organization: Colors.elm,
} as const;

const actionLabel = (state: StateKeys) => {
  switch (state) {
    case 'retract':
      return 'retracted';
    case 'accept':
      return 'accepted';
    case 'retract_redirect':
      return 'retracted forwarding';
    default:
      return `${state}d`;
  }
};

const errorLabel = (state: StateKeys) => {
  switch (state) {
    case 'retract_redirect':
      return 'retract';
    default:
      return state;
  }
};

export const PerformDisputeActionButton = ({
  disputeId,
  state,
  text,
  buttonType = 'button',
}: {
  disputeId: string;
  text: string;
  state: keyof typeof COLOR_STYLE_MAP;
  buttonType?: 'button' | 'text';
}) => {
  const refetchQueries = useDisputeRefetchs();
  const [performAction, { loading }] = useMutation(PerformDisputeAction);

  const changeDisputeState = useCallback(() => {
    if (loading) {
      return;
    }

    performAction({
      variables: {
        disputeId,
        input: {
          action: state,
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage('success', `Dispute ${actionLabel(state)}`);
        refetchQueries();
      })
      .catch(
        catchErrorWith(
          `Failed to ${errorLabel(state)} dispute. Please try again.`
        )
      );
  }, [disputeId, loading, performAction, refetchQueries, state]);

  return (
    <div className="PerformDisputeActionButton">
      {buttonType === 'button' ? (
        state === 'retract' || state === 'retract_redirect' ? (
          <BaseButton
            className="dispute-retract-button"
            onClick={changeDisputeState}
            size="large"
          >
            {text}
          </BaseButton>
        ) : (
          <Button
            className="dispute-action-button"
            size="large"
            type="primary"
            onClick={changeDisputeState}
          >
            {text}
          </Button>
        )
      ) : (
        <div onClick={changeDisputeState}>{text}</div>
      )}

      <style jsx>{`
        .PerformDisputeActionButton :global(.ant-btn.dispute-action-button) {
          background-color: ${COLOR_STYLE_MAP[state]};
          border: none;
          color: white;
        }
      `}</style>
    </div>
  );
};
